import { Dispatch, FC, SetStateAction, useState } from 'react';
import { ModalContextState, ModalProps } from 'modules/experiments/types';
import { useUnsavedChangesPrompt } from 'utils/hooks/useUnsavedChangesPrompt';
import { DestructiveButton, PrimaryButton, SecondaryButton, BUTTON_SIZES } from 'ui/button';
import { CircleSpinner } from 'ui/spinner';
import { Modal, ModalBody, ModalHeader, ModalFooter, MODAL_SIZES } from 'ui/modal';
import { colors } from 'ui/theme';
import { ModalContext } from './ModalContext';

const displayModal = (
  props: ModalProps,
  onClose: () => void,
  isConfirmActionInProgress: boolean,
  setIsConfirmActionInProgress: Dispatch<SetStateAction<boolean>>,
) => {
  const {
    id,
    size = MODAL_SIZES.SMALL,
    variant,
    title,
    body,
    buttonText,
    inProgressButtonText,
    isDestructive = true,
    onConfirm,
    secondaryButton,
  } = props;
  const Button = isDestructive ? DestructiveButton : PrimaryButton;
  const isConfirmAsync = !!inProgressButtonText;

  const onClick = () => {
    // This will change the onConfirm button to the in progress text
    // And, disables existing out of the modal
    if (isConfirmAsync) setIsConfirmActionInProgress(true);
    onConfirm();
  };

  return (
    <Modal
      data-test={`${id}-modal`}
      id={`${id}-modal`}
      isModalOpen={true}
      // When the confirm action is in progress
      // Don't let users to close the modal
      onClose={!isConfirmActionInProgress ? onClose : () => null}
      size={size}
      variant={variant}
    >
      <ModalHeader>{title}</ModalHeader>
      <ModalBody>{body}</ModalBody>
      <ModalFooter>
        {secondaryButton && (
          <SecondaryButton onClick={secondaryButton.onConfirm} size={BUTTON_SIZES.LARGE}>
            {secondaryButton.buttonText}
          </SecondaryButton>
        )}
        {/* Disable the onConfirm button when it is in progress. To disable multiple clicks */}
        <Button
          data-test={`confirm-${id}-button`}
          {...(!isConfirmActionInProgress ? { onClick } : {})}
          size={BUTTON_SIZES.LARGE}
        >
          {isConfirmActionInProgress ? (
            <>
              <CircleSpinner size={16} color={colors.white} /> {inProgressButtonText}
            </>
          ) : (
            <>{buttonText}</>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export const ModalContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [modalProps, setModalProps] = useState<ModalProps | null>(null);
  const [isConfirmActionInProgress, setIsConfirmActionInProgress] = useState(false);

  const closeModal = () => {
    setModalProps(null);
    setIsConfirmActionInProgress(false);
  };

  // Prevents page exists when the confirm action is in progress
  useUnsavedChangesPrompt(isConfirmActionInProgress);

  const context: ModalContextState = {
    hideModal: () => closeModal(),
    showModal: (props: ModalProps) => setModalProps(props),
  };

  return (
    <ModalContext.Provider value={context}>
      {children}
      {!!modalProps &&
        displayModal(
          modalProps,
          closeModal,
          isConfirmActionInProgress,
          setIsConfirmActionInProgress,
        )}
    </ModalContext.Provider>
  );
};
